import { apiPost } from "api/callApi";
import SC from "./style";
import InnoIcons from "InnoUI/innoicons";
import { Button, DetailDrawer, Popover, TableList } from "InnoUI/components";
import theme from "@theme";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import AddUpdateRevision from "../../AddUpdateRevision";
import DeleteComponent from "views/AdminSettings/DeleteComponent";
import { Skeleton } from "@mui/material";
import {
  byteToKB,
  formatFileSize,
  isValidFolderId,
  saveToLocalStorage,
  showAlert,
} from "utils/helper";
import TimezoneConverter from "components/TimeZoneConvertor";
import MoveFiles from "./MoveFiles";
import CheckPermissionWrapper from "components/CheckPermissionWrapper";
import { userAccessType } from "utils/lookUpTable";
import InfoRow from "components/InfoRow";
import ReadMoreText from "components/ReadMoreText";
import FileDetailsPrintModal from "../FileDetailsPrintModal";
import { ReadMoreHtmlText } from "@InnoInput/";
import { GeneratingText } from "components/GeneratingText";
import PointIdIndicator from "components/PointIdIndicator";
import { MAX_FILE_SIZE } from "utils/config";

const FileDetailsAction = (props) => {
  const {
    popupState,
    fileDetilsData,
    reloadData,
    getHistory,
    folderId,
    fileId,
    isDrawer = false,
  } = props;

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");
  const projectId = searchParams.get("projectId");
  const [openModalView, setOpenModalView] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [modalRowData, setModalRowData] = useState();
  const [isEditing, setIsEditing] = useState(false);
  const [openDeleteView, setOpenDeleteView] = useState(false);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [open, setOpen] = useState(false);
  const [deleteData, setDeleteData] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [data, setData] = useState();

  // Inside FileDetailsView
  const filteredDetailsData = useMemo(() => {
    return fileDetilsData.filter((data) => data.id === Number(fileId));
  }, [fileDetilsData, fileId]);

  useEffect(() => {
    if (filteredDetailsData.length > 0 && filteredDetailsData[0]) {
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [fileDetilsData, filteredDetailsData, fileId]);

  const getUrl = async (onlyUrl, expiresIn = 3600) => {
    try {
      const response = await apiPost(`/v1/file/downloadfile`, {
        filekey: filteredDetailsData[0].fileUrl,
        onlyUrl,
        expiresIn,
      });
      return response.fileUrl;
    } catch (error) {
      console.log("Error: " + error);
      return;
    }
  };

  const handleDownload = async (url, fileName) => {
    setDownloading(true);
    url = await getUrl();
    fileName = filteredDetailsData[0].fileName;
    fetch(url)
      .then((response) => response.blob())
      .then(async (blob) => {
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName || "downloaded-file";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        await getHistory();
        setDownloading(false);
      })
      .catch((error) => {
        console.error("Error fetching the file:", error);
        setDownloading(false);
      });
  };

  const handleSend = async (url) => {
    url = null;
    if (filteredDetailsData[0]?.fileSize > byteToKB(MAX_FILE_SIZE)) {
      url = await getUrl(1, 604800); // 604800 seconds = 7 days
    }
    if (filteredDetailsData[0]?.fileSize < byteToKB(MAX_FILE_SIZE)) {
      url = await getUrl(1, 3600); // 3600 seconds = 1 hour
    }

    const fileDetails = {
      fileName: filteredDetailsData[0]?.fileName,
      fileType: filteredDetailsData[0]?.fileType,
      fileSize: filteredDetailsData[0]?.fileSize * 1024,
      fileUrl: url,
      hideFile: true,
      fileId: fileId,
    };
    saveToLocalStorage("attachData", fileDetails);
    navigate(`/journal/add-journal?projectId=${projectId}&type=${type}`);
  };

  const closeDrawer = () => {
    setOpen(false);
  };

  const handleMove = () => {
    setOpen(true);
  };

  const handleBackClick = (data) => {
    if (!data[0].folderId || !data[0].projectId) {
      console.error("Folder ID or Project ID is missing");
      return;
    }
    // navigate(
    //   `/document?projectId=${data[0].projectId}&folderId=${data[0].folderId}&type=${type}`
    // );
    navigate(-1);
  };

  const handleUpdate = () => {
    setOpenModalView(true);
    setIsUpdate(true);
    setModalRowData(filteredDetailsData[0]);
    setIsEditing(false);
  };

  const handleEdit = (data) => {
    setOpenModalView(true);
    if (data) {
      setIsEditing(true);
      setIsUpdate(false);
      setModalRowData(filteredDetailsData[0]);
    }
  };

  const handleDelete = async (data) => {
    try {
      setDeleteData(true);
      const response = await apiPost(`/v1/file/deletefile/${fileId}`);
      showAlert({ title: "Success", text: response.message, icon: "success" });
      // navigate(
      //   `/document?projectId=${data[0].projectId}&folderId=${data[0].folderId}&type=${type}`
      // );
      window.location.href = `/document?projectId=${data[0].projectId}&folderId=${data[0].folderId}&type=${type}`;
      setDeleteData(false);
    } catch (error) {
      console.log("Error", error);
      setDeleteData(false);
    }
    setOpenDeleteView(false);
  };

  const handleDeleteOpenModal = () => {
    setOpenDeleteView(true);
  };

  const getBredcrumbsData = async () => {
    try {
      if (!isValidFolderId(folderId)) {
        setData("");
        return;
      }
      const result = await apiPost(`/v1/file/path/breadcrum/${folderId}`);
      setData(result.path);
    } catch (error) {
      console.log("Error", error);
    }
  };

  useEffect(() => {
    getBredcrumbsData();
  }, [folderId]);

  return (
    <>
      <div>
        {loading ? (
          Array.from({ length: 4 }).map((_, index) => (
            <Skeleton
              key={index}
              variant="rectangular"
              height={36}
              style={{ margin: "8px 15px" }}
            />
          ))
        ) : (
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "5px 10px 10px 10px",
                gap: "10px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                }}
              >
                {!isDrawer && (
                  <InnoIcons.ArrowBackIcon
                    sx={{
                      cursor: "pointer",
                      fontSize: "20px",
                      marginRight: "5px",
                    }}
                    onClick={() => handleBackClick(filteredDetailsData)}
                  />
                )}
                {/* Indicator Dot */}
                {filteredDetailsData[0]?.knowledgeStatus ? (
                  <PointIdIndicator
                    knowledgeStatus={filteredDetailsData[0]?.knowledgeStatus}
                  />
                ) : null}
                <span
                  style={{
                    color: theme.colors.themeColor,
                    marginLeft: isDrawer ? "10px" : "0px",
                    fontWeight: "bold",
                  }}
                >
                  {filteredDetailsData[0]?.subject}
                </span>
              </div>
              <div style={{ display: "flex", gap: "10px" }}>
                <CheckPermissionWrapper>
                  {({ permission }) => (
                    <>
                      {permission >= userAccessType.write ? (
                        <>
                          <SC.Button onClick={() => setShowModal(true)}>
                            View Details
                          </SC.Button>
                          <SC.Button onClick={handleMove}>Move</SC.Button>
                          <SC.Button onClick={handleSend}>
                            {filteredDetailsData[0]?.fileSize >=
                            byteToKB(MAX_FILE_SIZE)
                              ? "Send as link"
                              : "Send"}
                          </SC.Button>
                          {filteredDetailsData[0]?.isLatestRevision && (
                            <SC.Button onClick={handleEdit}>Edit</SC.Button>
                          )}
                          <SC.Button onClick={handleUpdate}>Update</SC.Button>
                          <SC.Button
                            onClick={handleDownload}
                            disabled={downloading ? true : false}
                          >
                            {downloading ? (
                              <GeneratingText label={"Downloading"} />
                            ) : (
                              "Download"
                            )}
                          </SC.Button>
                          <SC.Button onClick={handleDeleteOpenModal}>
                            {deleteData ? (
                              <GeneratingText label={"Deleting"} />
                            ) : (
                              "Delete"
                            )}
                          </SC.Button>
                        </>
                      ) : (
                        <SC.Button onClick={handleDownload}>Download</SC.Button>
                      )}
                    </>
                  )}
                </CheckPermissionWrapper>
              </div>
            </div>

            <SC.CardContainerStyle>
              <SC.RowContainerStyle>
                <InfoRow
                  label="File Name"
                  value={filteredDetailsData[0]?.subject}
                  maxLength={1000}
                />
                <InfoRow
                  label="Uploaded By"
                  value={filteredDetailsData[0]?.createdByName}
                  maxLength={1000}
                />
                <InfoRow
                  label="Original File Name"
                  value={filteredDetailsData[0]?.fileName}
                  maxLength={1000}
                />
                <InfoRow label="Uploaded Date & Time">
                  <TimezoneConverter
                    time={filteredDetailsData[0]?.createdOn || "N/A"}
                  />
                </InfoRow>

                <InfoRow
                  label="Type of Document"
                  value={filteredDetailsData[0]?.fileType}
                />
                <InfoRow
                  label="Size"
                  value={formatFileSize(filteredDetailsData[0]?.fileSize)}
                />

                <InfoRow label="Document Summary">
                  {/* <ReadMoreText
                    text={filteredDetailsData[0]?.noteSearchableText || "N/A"}
                    maxChars={400}
                    htmlData={filteredDetailsData[0]?.note || "N/A"}
                  /> */}

                  <ReadMoreHtmlText
                    html={filteredDetailsData[0]?.note || "N/A"}
                    maxChars={200}
                  />
                </InfoRow>
              </SC.RowContainerStyle>
            </SC.CardContainerStyle>
          </div>
        )}
      </div>

      {openModalView && (
        <AddUpdateRevision
          setOpenModalView={setOpenModalView}
          openModalView={openModalView}
          isUpdate={isUpdate}
          setIsUpdate={setIsUpdate}
          projectId={filteredDetailsData[0].projectId}
          folderId={filteredDetailsData[0].folderId}
          parentId={filteredDetailsData[0].parentId}
          folderData={modalRowData}
          setModalRowData={setModalRowData}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          reloadData={reloadData}
          getHistory={getHistory}
        />
      )}
      {openDeleteView && (
        <DeleteComponent
          title={"Delete Document"}
          messageData={`Are you sure you want to Delete this document ${
            filteredDetailsData[0].fileName || ""
          }?`}
          setOpenDeleteView={setOpenDeleteView}
          openDeleteView={openDeleteView}
          handleDelete={() => handleDelete(filteredDetailsData)}
          handleArchive={() => setOpenDeleteView(false)}
          firstButtontext={"Cancel"}
          isLoading={deleteData}
        />
      )}

      {open && (
        <DetailDrawer
          open={open}
          onClose={closeDrawer}
          title={false}
          closable={false}
          width={"970px"}
          children={
            <MoveFiles
              onClose={closeDrawer}
              fileData={filteredDetailsData[0]}
            />
          }
        ></DetailDrawer>
      )}

      {showModal && (
        <FileDetailsPrintModal
          setOpenModalView={setShowModal}
          openModalView={showModal}
          fileData={filteredDetailsData[0]}
          pathData={data}
        />
      )}
    </>
  );
};

export default FileDetailsAction;
